import { useState } from 'react';

import { KycDataDialog } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycDataDialog/KycDataDialog';
import { KycResult } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/components/DocumentIdSection/components/KycResult';
import { useBorrowerKYC } from '@/components/modules/KYC/KYCLoan/BorrowerKYC/hooks/useBorrowerKYC';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { FormGridOptions } from '@/components/shared/components/FormGridContainer/types/formGridOptions';
import { ActionsOption } from '@/components/shared/components/MoreActionsMenu/types/ActionsOption';
import { AutocompleteNationalityFormField } from '@/components/shared/components/NewContextForms/AutocompleteNationalityFormField/AutocompleteNationalityFormField';
import { DateFormField } from '@/components/shared/components/NewContextForms/DateFormField';
import { TextFormField } from '@/components/shared/components/NewContextForms/TextFormField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { SubSectionWrapper } from '@/components/shared/components/SubSectionWrapper';
import { Text } from '@/components/shared/components/Text';
import { useDialog } from '@/components/shared/hooks/useDialog';
import { CreditCardBorrowerOnboardingDetails, PendingDataType } from '@/config/types';

interface DocumentIdSectionProps extends FormGridOptions {
  creditCardId: string;
  onboardingDetails: CreditCardBorrowerOnboardingDetails;
  editModeAllowed?: boolean;
}

export const DocumentIdSection = ({ creditCardId, onboardingDetails, fullWidth }: DocumentIdSectionProps) => {
  const fieldVariant = FieldVariant.dataRow;

  const { opened, setOpened } = useDialog();

  const { filteredKyc } = useBorrowerKYC({
    externalId: creditCardId,
    onboardingDetails,
  });

  const [dialogAction, setDialogAction] = useState<DocumentIdAction | undefined>(undefined);

  const documentIdActionsOptions: ActionsOption[] = [
    ...(filteredKyc && filteredKyc.length > 0
      ? [
          {
            display: <Text tId={'SHOW_KYC_DATA'} />,
            onClick: () => {
              setOpened(true);
              setDialogAction(DocumentIdAction.CHECK_KYC_DATA);
            },
            iconName: 'fingerprint',
          },
        ]
      : []),
  ];

  if (!onboardingDetails.documentId) {
    return (
      <SubSectionWrapper title="PERSONAL_DATA">
        <PendingDataWarning type={PendingDataType.DEFAULT} />
      </SubSectionWrapper>
    );
  }

  return (
    <SubSectionWrapper
      title="PERSONAL_DATA"
      leftComponent={filteredKyc && filteredKyc?.length > 0 ? <KycResult kyc={filteredKyc[0]} /> : <></>}
      specificActionsOptionsArray={documentIdActionsOptions?.length > 0 ? documentIdActionsOptions : undefined}
    >
      <FormGridContainer fieldVariant={fieldVariant} fullWidth={fullWidth}>
        <TextFormField
          label={'FIRST_NAME'}
          name={'firstName'}
          value={onboardingDetails.documentId.fullName.firstName}
        />
        <TextFormField label={'LAST_NAME'} name={'lastName'} value={onboardingDetails.documentId.fullName.lastName} />
        <TextFormField
          label={'DOCUMENT_ID_NUMBER'}
          name={'documentNumber'}
          value={onboardingDetails.documentId.documentNumber}
        />
        <DateFormField
          name={'documentExpirationDate'}
          label={'DOCUMENT_EXPIRATION_DATE'}
          value={onboardingDetails.documentId.expiration}
          disableFuture
        />
        <AutocompleteNationalityFormField
          name={'nationality'}
          label={'NATIONALITY'}
          value={onboardingDetails.documentId.nationality}
          fieldVariant={fieldVariant}
        />
        <DateFormField
          name={'birthDate'}
          label={'BIRTH_DATE'}
          value={onboardingDetails.documentId.birthDate}
          disableFuture
        />
        <AutocompleteNationalityFormField
          name={'birthNationality'}
          label={'BIRTH_NATIONALITY'}
          value={onboardingDetails.documentId.birthNationality}
          fieldVariant={fieldVariant}
        />
        <TextFormField label={'GENDER'} name={'gender'} value={onboardingDetails.documentId.gender} />
      </FormGridContainer>
      {filteredKyc && opened && dialogAction === DocumentIdAction.CHECK_KYC_DATA && (
        <KycDataDialog
          opened={opened}
          setOpened={setOpened}
          setActionDialogParams={() => {
            setDialogAction(undefined);
          }}
          kyc={filteredKyc}
        />
      )}
    </SubSectionWrapper>
  );
};

export enum DocumentIdAction {
  CHECK_KYC_DATA = 'check_kyc_data',
}
